// The shipwreck that ends a lost game: explosions below deck, a blast amidships, the hull
// breaking in two, both halves rearing up and sliding under, wreckage left floating.
//
// The ship is rendered twice, each copy clipped to one side of the break plane and moved on its
// own. Everything runs on one clock (seconds since the wreck began; 99 when the page opens on a
// game that was already lost, so the ship is simply gone).
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import * as THREE from "three";

import { kDeckY, spaceCentre } from "./coords";
import { Burst, Fire, Plume } from "./fx";

type Clock = React.MutableRefObject<number>;
type Side = "bow" | "stern";

/** When the main blast splits the hull. */
const kBreakAt = 1.0;
/** Halves turn about a point just under the waterline at the break. */
const kPivotY = -0.6;
/** By then both halves are under (stop drawing them). */
const kGoneAt = 11;

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const smooth = (x: number) => { const t = clamp01(x); return t * t * (3 - 2 * t); };

/** The wreck clock (−1 = afloat) and the ship's presence on the water (1 → 0) for the ocean. */
export function useWreckClock(wrecking: boolean): { clock: Clock; ship: Clock } {
  const clock = useRef(wrecking ? 99 : -1);
  const ship = useRef(wrecking ? 0 : 1);
  const first = useRef(true);
  useEffect(() => {
    clock.current = wrecking ? (first.current ? 99 : 0) : -1;
    first.current = false;
  }, [wrecking]);
  useFrame((_, dt) => {
    if (clock.current >= 0) clock.current += Math.min(dt, 0.1);
    ship.current = clock.current < 0 ? 1 : 1 - smooth((clock.current - kBreakAt - 0.5) / 7);
  });
  return { clock, ship };
}

/** Where one half is at time t: drift apart, rear up (broken end down) and sink, faster and faster. */
function pose(side: Side, t: number): { dx: number; y: number; rot: number; roll: number } {
  const tb = t - kBreakAt;
  if (tb <= 0) return { dx: 0, y: 0, rot: 0, roll: 0 };
  const sgn = side === "bow" ? -1 : 1;
  const tilt = (side === "bow" ? 0.95 : 1.12) * smooth(tb / 4.2);
  const jolt = tb < 0.6 ? 0.45 * Math.sin((tb / 0.6) * Math.PI) : 0;
  return {
    dx: sgn * 1.8 * (1 - Math.exp(-tb * 1.2)),
    y: jolt - (0.25 * tb + 0.2 * tb * tb),
    rot: sgn * tilt, // bow (−x) turns negative, stern positive: both tips rise
    roll: sgn * 0.14 * smooth(tb / 3),
  };
}

/** The break: a slightly slanted plane through midships. The bow keeps one side, the stern the other. */
const kBreak = new THREE.Plane(new THREE.Vector3(-1, 0, 0.18).normalize(), 0);

/** Give every material under `root` a private copy clipped by `plane` (once per material). */
function clipAll(root: THREE.Object3D, plane: THREE.Plane) {
  root.traverse(o => {
    const holder = o as THREE.Mesh;
    const m = holder.material;
    if (!m) return;
    const patch = (mm: THREE.Material) => {
      if (mm.userData.wreckClip) return mm;
      const c = mm.clone();
      c.clippingPlanes = [plane];
      c.clipShadows = true;
      // Show the inside of the cut hull instead of seeing through it.
      if ((o as THREE.Mesh).isMesh) c.side = THREE.DoubleSide;
      c.userData.wreckClip = true;
      return c;
    };
    holder.material = Array.isArray(m) ? m.map(patch) : patch(m);
  });
}

/** One half of the ship: children are a full ship, clipped to this side of the break. */
export function ShipHalf({ side, clock, children }: { side: Side; clock: Clock; children: ReactNode }) {
  const outer = useRef<THREE.Group>(null);
  const inner = useRef<THREE.Group>(null);
  const plane = useMemo(() => new THREE.Plane(), []);
  const base = useMemo(() => (side === "bow" ? kBreak.clone() : kBreak.clone().negate()), [side]);
  const sincePatch = useRef(1);
  useFrame((_, dt) => {
    const o = outer.current, i = inner.current;
    if (!o || !i) return;
    // Children may mount later (fire, pieces): re-patch now and then; patched materials are skipped.
    sincePatch.current += dt;
    if (sincePatch.current > 0.3) { sincePatch.current = 0; clipAll(i, plane); }
    const t = clock.current;
    o.visible = t < kGoneAt;
    const p = pose(side, t);
    o.position.set(p.dx, kPivotY + p.y, 0);
    o.rotation.set(p.roll, 0, p.rot);
    i.updateWorldMatrix(true, false);
    plane.copy(base).applyMatrix4(i.matrixWorld);
  });
  return (
    <group ref={outer} position={[0, kPivotY, 0]}>
      <group ref={inner} position={[0, -kPivotY, 0]}>{children}</group>
    </group>
  );
}

/** Flames and smoke pouring from the broken end of a half, from the blast until it is under. */
export function BreakFire({ side, clock }: { side: Side; clock: Clock }) {
  const ref = useRef<THREE.Group>(null);
  useFrame(() => {
    const t = clock.current;
    if (ref.current) ref.current.visible = t > kBreakAt - 0.05 && t < kGoneAt;
  });
  const x = side === "bow" ? -0.9 : 0.9;
  return (
    <group ref={ref} visible={false}>
      <Fire position={[x, kDeckY - 0.2, -1.6]} seed={side === "bow" ? 3 : 9} />
      <Fire position={[x, kDeckY - 0.2, 1.4]} seed={side === "bow" ? 21 : 27} />
      <Plume position={[x, kDeckY + 0.5, 0]} count={26} color="#1b1816" spread={2.2} height={11} size={3.2} speed={1.4} opacity={0.7} seed={side === "bow" ? 5 : 15} />
    </group>
  );
}

// ---------------------------------------------------------------- one-shot effects
interface Shot { key: number; position: [number, number, number]; color: string; count?: number; life?: number; size?: number; spread?: number; up?: number; additive?: boolean }
interface Imp { flash: React.MutableRefObject<number>; shake: React.MutableRefObject<number> }

const at = (sp: Parameters<typeof spaceCentre>[0], y = kDeckY + 0.4): [number, number, number] => {
  const [x, z] = spaceCentre(sp);
  return [x, y, z];
};
const mid: [number, number, number] = [0, kDeckY + 0.6, 0];

const kTimeline: { at: number; run: (imp: Imp) => Omit<Shot, "key">[] }[] = [
  { at: 0.05, run: imp => { imp.shake.current = 0.6; return [
    { position: at("boiler"), color: "#ff8a3a", spread: 2.5, up: 4, size: 1.2, count: 30 },
    { position: at("boiler", kDeckY + 0.8), color: "#16110d", spread: 1.6, up: 5, size: 2, life: 2.6, count: 24, additive: false },
  ]; } },
  { at: 0.5, run: imp => { imp.shake.current = 0.9; return [
    { position: at("cargo"), color: "#ffa04a", spread: 2.8, up: 4.5, size: 1.3, count: 30 },
  ]; } },
  // The blast: fireball, sparks, black smoke, a wall of spray.
  { at: kBreakAt, run: imp => { imp.flash.current = 1; imp.shake.current = 2.2; return [
    { position: mid, color: "#ff7a1f", spread: 5.5, up: 7, size: 3.2, life: 2.2, count: 70 },
    { position: mid, color: "#ffe28a", spread: 10, up: 12, size: 0.45, life: 2.4, count: 80 },
    { position: [0, kDeckY + 1.5, 0], color: "#120e0b", spread: 3.5, up: 6, size: 4.2, life: 4.5, count: 40, additive: false },
    { position: [0, 0.2, -3.6], color: "#e8f2f2", spread: 3.2, up: 7, size: 1.6, life: 2.2, count: 40, additive: false },
    { position: [0, 0.2, 3.6], color: "#e8f2f2", spread: 3.2, up: 7, size: 1.6, life: 2.2, count: 40, additive: false },
  ]; } },
  // Spray where the sea pours in, then the tips going under, then bubbles.
  ...[1.9, 2.8, 3.8, 4.9, 6.1].map((t, i) => ({ at: t, run: () => [
    { position: [(i % 2 ? 1 : -1) * (2 + i), 0.2, (i % 2 ? -1 : 1) * 3.2] as [number, number, number], color: "#dfeeee", spread: 2.6, up: 4.5, size: 1.4, life: 1.8, count: 30, additive: false },
  ] })),
  { at: 7.4, run: imp => { imp.shake.current = 0.6; return [
    { position: [-6, 0.3, 0], color: "#e6f2f2", spread: 4, up: 9, size: 2, life: 2.4, count: 46, additive: false },
    { position: [6.5, 0.3, 0], color: "#e6f2f2", spread: 4, up: 9, size: 2, life: 2.4, count: 46, additive: false },
  ]; } },
  ...[8.3, 9.0, 9.8, 10.7, 11.7].map((t, i) => ({ at: t, run: () => [
    { position: [(i % 2 ? 1 : -1) * (3 + i * 0.7), 0.15, (i - 2) * 0.9] as [number, number, number], color: "#cfe6e6", spread: 1.2, up: 1.6, size: 0.6, life: 1.6, count: 16, additive: false },
  ] })),
];

/** Bursts on cue, the blast's light, and the floating wreckage. */
export function WreckFx({ clock, imp }: { clock: Clock; imp: Imp }) {
  const [shots, setShots] = useState<Shot[]>([]);
  const prev = useRef(clock.current);
  const light = useRef<THREE.PointLight>(null);
  useFrame(() => {
    const t = clock.current, p = prev.current;
    prev.current = t;
    if (light.current) light.current.intensity = t > kBreakAt ? 140 * Math.exp(-(t - kBreakAt) * 2.2) + (t < 7 ? 8 : 0) : t > 0 ? 25 * Math.exp(-((t % 0.45) * 6)) : 0;
    if (t < 0 || t <= p) return;
    // Only cues just crossed (a page opened on a sunk ship skips them all).
    const due = kTimeline.filter(e => p < e.at && e.at <= t && t - e.at < 0.5);
    if (due.length === 0) return;
    const add = due.flatMap((e, i) => e.run(imp).map((s, j) => ({ ...s, key: e.at * 1000 + i * 10 + j })));
    setShots(s => [...s, ...add]);
    setTimeout(() => setShots(s => s.filter(x => !add.includes(x))), 5000);
  });
  return (
    <>
      <pointLight ref={light} position={[0, kDeckY + 2, 0]} color="#ff8a3a" distance={30} decay={1.6} intensity={0} />
      {shots.map(({ key, ...s }) => <Burst key={key} seed={Math.round(key)} {...s} />)}
      <Wreckage clock={clock} />
    </>
  );
}

function rand(seed: number): () => number {
  let s = seed;
  return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}

/** Planks and crates thrown out by the blast; they land on the sea and stay afloat. */
function Wreckage({ clock }: { clock: Clock }) {
  const parts = useMemo(() => {
    const r = rand(7);
    return Array.from({ length: 18 }, (_, i) => {
      const a = r() * Math.PI * 2, s = 2 + r() * 4.5;
      const crate = i % 5 === 0;
      return {
        v: [Math.cos(a) * s * 0.7, 5 + r() * 6, Math.sin(a) * s] as const,
        spin: [r() * 8 - 4, r() * 8 - 4, r() * 8 - 4] as const,
        dims: (crate ? [0.45, 0.45, 0.45] : [0.9 + r() * 0.9, 0.08, 0.22 + r() * 0.12]) as [number, number, number],
        color: crate ? "#6b4a2a" : r() < 0.5 ? "#5a3c22" : "#3b2a1c",
        yaw: r() * Math.PI,
        phase: r() * 6,
      };
    });
  }, []);
  const refs = useRef<(THREE.Mesh | null)[]>([]);
  useFrame(({ clock: c }) => {
    const te = clock.current - kBreakAt;
    parts.forEach((p, i) => {
      const m = refs.current[i];
      if (!m) return;
      m.visible = te > 0;
      if (te <= 0) return;
      const y0 = kDeckY + 0.6, rest = 0.22;
      const land = (p.v[1] + Math.sqrt(p.v[1] * p.v[1] + 2 * 9.8 * (y0 - rest))) / 9.8;
      if (te < land) {
        m.position.set(p.v[0] * te, y0 + p.v[1] * te - 4.9 * te * te, p.v[2] * te);
        m.rotation.set(p.spin[0] * te, p.yaw + p.spin[1] * te, p.spin[2] * te);
      } else {
        const drift = Math.min(te - land, 40) * 0.12;
        const bob = c.elapsedTime * 1.4 + p.phase;
        m.position.set(p.v[0] * land + drift, rest + Math.sin(bob) * 0.1, p.v[2] * land);
        m.rotation.set(Math.sin(bob) * 0.12, p.yaw, Math.cos(bob * 0.8) * 0.1);
      }
    });
  });
  return (
    <>
      {parts.map((p, i) => (
        <mesh key={i} ref={el => { refs.current[i] = el; }} visible={false} castShadow>
          <boxGeometry args={p.dims} />
          <meshStandardMaterial color={p.color} roughness={0.9} />
        </mesh>
      ))}
    </>
  );
}
